import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  Container,
  Paper,
  IconButton,
  CircularProgress,
  Popper,
} from "@mui/material";
import { Typography, TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import ResponsiveDialogManager from "../../components/dialog/ResponsiveDialogManager";
import ConfirmationDialog from "../../components/dialog/ConfirmationDialog";
import ToolbarDefaultActions from "../../components/dialog/toolbarDefaultActions";
import AddIcon from "@mui/icons-material/Add";
import CreateCategoryForm from "../../components/forms/CreateCategoryForm";
import LinkSubCategories from "../../components/forms/LinkSubCategories";
import { categoryService } from "../../services";
import ClearIcon from "@mui/icons-material/Clear";

const PREFIX = "Categories";

const classes = {
  mainContainer: `${PREFIX}-mainContainer`,
  noDisplayContainer: `${PREFIX}-noDisplayContainer`,
  filtersContainer: `${PREFIX}-filtersContainer`,
  searchAutocomplete: `${PREFIX}-searchAutocomplete`,
  categoriesContainer: `${PREFIX}-categoriesContainer`,
  itemContainer: `${PREFIX}-itemContainer`,
  deleteIcon: `${PREFIX}-deleteIcon`,
  categoryIcon: `${PREFIX}-categoryIcon`,
  categoryTitle: `${PREFIX}-categoryTitle`,
  addButton: `${PREFIX}-addButton`,
  selectMenuStyle: `${PREFIX}-selectMenuStyle`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`&.${classes.mainContainer}`]: {
    paddingTop: "1rem",
  },

  [`& .${classes.noDisplayContainer}`]: {
    marginTop: "2rem",
  },

  [`& .${classes.filtersContainer}`]: {
    display: "flex",
    width: "100%",
    maxWidth: "350px",
    margin: "0 auto",
  },

  [`& .${classes.searchAutocomplete}`]: {
    minWidth: "80%",
    marginRight: "1rem",
  },

  [`& .${classes.categoriesContainer}`]: {
    marginTop: "2rem",
    maxHeight: "500px",
    display: "flex",
    padding: "1rem 0 0 1rem",
    flexWrap: "wrap",
    justifyContent: "center",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
  },

  [`& .${classes.itemContainer}`]: {
    backgroundColor: "#424242",
    width: "140px",
    height: "140px",
    marginRight: "1rem",
    marginBottom: "1rem",
    display: "flex",
    flexDirection: "column",
    padding: "0.5rem",
    position: "relative",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      cursor: "pointer",
    },
    "&:hover > span": {
      color: "#434343",
    },
  },

  [`& .${classes.deleteIcon}`]: {
    position: "absolute",
    right: "-0.5rem",
    top: "-0.5rem",
  },

  [`& .${classes.categoryIcon}`]: {
    width: "90px",
    height: "90px",
    borderRadius: "50%",
    objectFit: "cover",
    margin: "0 auto",
    marginBottom: "0.5rem",
  },

  [`& .${classes.categoryTitle}`]: {
    margin: "0 auto",
    maxWidth: "100px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  [`& .${classes.addButton}`]: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: "50%",
    height: "55px",
    width: "55px",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      opacity: "0.9",
    },
  },

  [`& .${classes.selectMenuStyle}`]: {
    "& > div": {
      backgroundColor: "#424242 !important",
    },
  },
}));

const Categories = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [categories, setCategories] = useState(null);
  const [filteredCategories, setFilteredCategories] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [fetchCategories, setFetchCategories] = useState(false);
  const [fetchTags, setFetchTags] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [tags, setTags] = useState(null);
  const [updateLoader, setUpdateLoader] = useState(false);
  const [updateCategoryTags, setUpdateCategoryTags] = useState(null);
  const [updateCategoryInfos, setUpdateCategoryInfos] = useState(null);
  const [triggerUpdate, setTriggerUpdate] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [isCreate, setIsCreate] = useState(false);

  useEffect(() => {
    if (updateCategoryInfos && updateCategoryTags) {
      setUpdateLoader(true);
      let logo = null;
      let imageFile = null;
      if (typeof updateCategoryInfos.logo !== "string") {
        logo = updateCategoryInfos.logo;
        delete updateCategoryInfos.logo;
      }
      if (typeof updateCategoryInfos.image !== "string") {
        imageFile = updateCategoryInfos.image;
        delete updateCategoryInfos.image;
      }
      categoryService.updateCategory(
        { ...updateCategoryInfos, hashtags: updateCategoryTags },
        logo,
        imageFile,
        () =>
          enqueueSnackbar(t("toasts.updateCategoryError"), {
            variant: "error",
          }),
        () =>
          enqueueSnackbar(t("toasts.updateCategorySuccess"), {
            variant: "success",
          }),
        () => setTriggerUpdate(false),
        () => setSelectedCategory(null),
        () => setUpdateLoader(false)
      );
    }
  }, [updateCategoryTags, updateCategoryInfos]);

  useEffect(() => {
    setFetchCategories(true);
    setFetchTags(true);
    categoryService.fetchCategories().then((res) => {
      setCategories(res);
      setFetchCategories(false);
    });
    categoryService.fetchTags().then((res) => {
      setTags(res);
      setFetchTags(false);
    });
  }, []);

  const handleSearch = (data) => {
    if (!data) setFilteredCategories(null);
    else
      setFilteredCategories(
        categories.filter((item) => item.name === data.name)
      );
  };

  useEffect(() => {
    if (!selectedCategory && categories && tags) {
      categoryService.fetchCategories().then((res) => {
        setCategories(res);
        setFetchCategories(false);
      });
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (!isConfirm) {
      setCategoryToDelete(null);
    }
  }, [isConfirm]);

  const Category = (props) => (
    <StyledDiv>
      <Paper
        elevation={2}
        className={classes.itemContainer}
        onClick={() => setSelectedCategory(props.item)}
      >
        <IconButton
          color="primary"
          aria-label="upload picture"
          component="span"
          className={classes.deleteIcon}
          onClick={(e) => {
            e.stopPropagation();
            setCategoryToDelete(props.item);
            setIsConfirm(true);
          }}
          size="large"
        >
          <ClearIcon />
        </IconButton>
        <img
          alt={props.item.name}
          src={props.item.logo}
          className={classes.categoryIcon}
        />
        <Typography variant="body2" className={classes.categoryTitle}>
          {props.item.name}
        </Typography>
      </Paper>
    </StyledDiv>
  );

  return (
    <StyledDiv>
      <Container className={classes.mainContainer}>
        <h1>{t("categories.title")}</h1>
        <div>
          <ResponsiveDialogManager
            onClose={() => setIsCreate(false)}
            open={isCreate}
            renderer={
              <CreateCategoryForm
                closeModal={() => setIsCreate(false)}
                refreshCategories={() => {
                  categoryService
                    .fetchCategories()
                    .then((res) => setCategories(res));
                }}
              />
            }
            title={t("categories.createCategoryTitle")}
          />
          <div className={classes.filtersContainer}>
            <Autocomplete
              id="search-categories-autocomplete"
              className={classes.searchAutocomplete}
              options={categories || []}
              getOptionLabel={(option) =>
                option.name.length > 30
                  ? option.name.substr(0, 30) + "..."
                  : option.name
              }
              onChange={(e, data) => handleSearch(data)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("categories.searchCategoryAutocomplete")}
                  variant="outlined"
                />
              )}
              PopperComponent={(props) => (
                <Popper {...props} className={classes.selectMenuStyle} />
              )}
            />
            <IconButton
              aria-label="more"
              aria-controls="long-menu"
              aria-haspopup="true"
              color="primary"
              component="span"
              className={classes.addButton}
              onClick={() => setIsCreate(true)}
              size="large"
            >
              <AddIcon style={{ color: "#000" }} />
            </IconButton>
          </div>
          {fetchCategories || fetchTags ? (
            <div className="loading-container">
              <CircularProgress size={50} />
              <Typography>{t("loaders.fetching")}</Typography>
            </div>
          ) : (
            <div className={classes.categoriesContainer}>
              <ResponsiveDialogManager
                onClose={() => setSelectedCategory(null)}
                open={selectedCategory ? true : false}
                disableGutters
                noClose={updateLoader}
                renderer={
                  <LinkSubCategories
                    selectedCategory={selectedCategory}
                    tags={tags}
                    setUpdateCategoryTags={setUpdateCategoryTags}
                    setUpdateCategoryInfos={setUpdateCategoryInfos}
                    triggerUpdate={triggerUpdate}
                  />
                }
                title={t("categories.link.title")}
                toolbarActions={
                  selectedCategory && !updateLoader ? (
                    <ToolbarDefaultActions
                      save={() => setTriggerUpdate(true)}
                    />
                  ) : selectedCategory && updateLoader ? (
                    <CircularProgress size={28} style={{ color: "black" }} />
                  ) : null
                }
              />
              <ConfirmationDialog
                open={isConfirm}
                setOpen={setIsConfirm}
                element={categoryToDelete}
                onAgree={() => {
                  categoryService.deleteCategory(
                    categoryToDelete._id,
                    () =>
                      enqueueSnackbar(t("toasts.deleteCategorySuccess"), {
                        variant: "success",
                      }),
                    () =>
                      enqueueSnackbar(t("toasts.deleteCategoryError"), {
                        variant: "error",
                      }),
                    () => {
                      categoryService.fetchCategories().then((res) => {
                        setCategories(res);
                        setFetchCategories(false);
                      });
                    }
                  );
                }}
                mode="deleteCategory"
              />
              {filteredCategories && filteredCategories.length > 0 ? (
                filteredCategories.map((item, index) => (
                  <Category item={item} key={`${item.name}-${index + 1}`} />
                ))
              ) : categories && categories.length > 0 ? (
                categories.map((item, index) => (
                  <Category item={item} key={`${item.name}-${index + 1}`} />
                ))
              ) : (
                <div className={classes.noDisplayContainer}>
                  <Typography variant="body1">
                    {t("categories.noCategories")}
                  </Typography>
                </div>
              )}
            </div>
          )}
        </div>
      </Container>
    </StyledDiv>
  );
};

export default Categories;
